import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Check } from 'lucide-react';
import ArrayStep from '../components/sensorBox/ArrayStep';
import BoxStep from '../components/sensorBox/BoxStep';
import LocationStep from '../components/sensorBox/LocationStep';
import type { ResolvedBox } from '../services/sensorBoxBoxes';
import type { ResolvedLocation } from '../services/sensorBoxLocations';

const steps = [
  { id: 1, label: 'Location' },
  { id: 2, label: 'Box' },
  { id: 3, label: 'Sensor array' },
] as const;

type StepId = (typeof steps)[number]['id'];

export default function SensorBox() {
  const [currentStep, setCurrentStep] = useState<StepId>(1);
  const [resolvedLocation, setResolvedLocation] = useState<ResolvedLocation | null>(null);
  const [resolvedBox, setResolvedBox] = useState<ResolvedBox | null>(null);

  const resetWizard = () => {
    setResolvedLocation(null);
    setResolvedBox(null);
    setCurrentStep(1);
  };

  const clearLocation = () => {
    resetWizard();
  };

  const selectLocation = (location: ResolvedLocation) => {
    setResolvedLocation(location);
    setResolvedBox(null);
    setCurrentStep(2);
  };

  const selectBox = (box: ResolvedBox) => {
    setResolvedBox(box);
    setCurrentStep(3);
  };

  const clearBox = () => {
    setResolvedBox(null);
    setCurrentStep(2);
  };

  const intro =
    currentStep === 1
      ? 'Start by choosing where the box will be deployed. Existing coordinates are reused so duplicate sites are not created.'
      : currentStep === 2
        ? 'Assign a sensor box to this location. You can reuse a box already at the site or define a new one.'
        : 'Choose an existing array specification or define the sensors for a new one.';

  return (
    <div className="bg-gradient-to-b from-[#E6F7F4] to-[#C5E8E3] min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-10 sm:py-16">
        <Helmet>
          <title>SensorBox | BIOCAP Website</title>
          <meta
            name="description"
            content="Create and manage BIOCAP sensor boxes, locations, and sensor arrays."
          />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        </Helmet>
        <h1 className="text-4xl font-bold mb-3">SensorBox</h1>
        <p className="text-gray-600 text-lg mb-8">{intro}</p>

        <ol className="flex flex-wrap gap-2 sm:gap-3 mb-8">
          {steps.map((step) => {
            const current = step.id === currentStep;
            const done =
              (step.id === 1 && resolvedLocation !== null && currentStep > 1) ||
              (step.id === 2 && resolvedBox !== null && currentStep > 2);
            const reachable =
              step.id === 1 ||
              (step.id === 2 && resolvedLocation !== null) ||
              (step.id === 3 && resolvedBox !== null);
            return (
              <li key={step.id}>
                <button
                  type="button"
                  onClick={() => {
                    if (step.id === 1) setCurrentStep(1);
                    if (step.id === 2 && resolvedLocation) setCurrentStep(2);
                    if (step.id === 3 && resolvedBox) setCurrentStep(3);
                  }}
                  disabled={!reachable}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 sm:px-4 py-1.5 text-sm transition-colors ${
                    current
                      ? 'bg-[#005E6E] text-white'
                      : done
                        ? 'bg-teal-100 text-teal-800'
                        : reachable
                          ? 'bg-white text-gray-700 hover:bg-white'
                          : 'bg-white/70 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {done ? <Check className="h-3.5 w-3.5" aria-hidden /> : <span>{step.id}.</span>}
                  {step.label}
                </button>
              </li>
            );
          })}
        </ol>

        {currentStep >= 2 && resolvedLocation && (
          <p className="mb-2 text-sm text-gray-700 break-words">
            Location:{' '}
            <span className="font-medium">{resolvedLocation.siteName}</span>
            {' · '}ID {resolvedLocation.id}
          </p>
        )}
        {currentStep === 3 && resolvedBox && (
          <p className="mb-4 text-sm text-gray-700 break-words">
            Box: <span className="font-medium">{resolvedBox.nickname}</span>
            {' · '}ID {resolvedBox.id}
          </p>
        )}

        {currentStep === 1 && (
          <LocationStep
            resolved={resolvedLocation}
            onResolved={selectLocation}
            onClear={clearLocation}
            onContinue={() => setCurrentStep(2)}
          />
        )}

        {currentStep === 2 && resolvedLocation && (
          <BoxStep
            location={resolvedLocation}
            resolved={resolvedBox}
            onResolved={selectBox}
            onClear={clearBox}
            onContinue={() => resolvedBox && setCurrentStep(3)}
          />
        )}

        {currentStep === 3 && resolvedBox && (
          <ArrayStep box={resolvedBox} onCreateAnother={resetWizard} />
        )}
      </div>
    </div>
  );
}
